// UsersDashboardAdmin.js
// Mirrors RecordsDashboardAdmin's CircularProgress pattern so the Users
// page dashboard looks and behaves consistently with the Records page.
//
// Unlike RecordsDashboardAdmin, this does NOT do its own fetch - the Users
// page already loads the full contacts array for the table, so we just
// derive the stats from that (props) instead of hitting the API a second
// time for numbers we already have in memory.

import React from "react";

// Reusable Circular Progress Component (identical shape/props to the one
// used in RecordsDashboardAdmin, kept local here so this file has no
// dependency on the records folder).
function CircularProgress({ value, max, label, color }) {
  const radius = 30;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radius;
  const progress = max > 0 ? (value / max) * circumference : 0;

  return (
    <div className="tc">
      <svg width="120" height="120" className="mb0">
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx="60"
          cy="60"
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
        <text x="60" y="65" textAnchor="middle" className="f4 fw6 dark-gray">
          {value}
        </text>
      </svg>
      <div className="f6 gray">{label}</div>
    </div>
  );
}

export default function UsersDashboardAdmin({ users = [], loading }) {
  if (loading) return <div className="pa4 gray">Loading...</div>;

  const totalUsers = users.length;

  const activeUsers = users.filter(
    (u) => String(u?.status || "").toLowerCase() === "active"
  ).length;

  const pendingUsers = users.filter(
    (u) => String(u?.status || "").toLowerCase() === "pending"
  ).length;

  const disabledUsers = users.filter(
    (u) => String(u?.status || "").toLowerCase() === "disabled"
  ).length;

  // Dynamic scaling, same approach as RecordsDashboardAdmin so the rings
  // never look empty/full for small or large user counts.
  const totalMax = totalUsers > 0 ? totalUsers * 1.2 : 10;
  const activeMax = totalUsers > 0 ? totalUsers * 1.2 : 10;
  const pendingMax = totalUsers > 0 ? totalUsers * 1.2 : 10;

  return (
    <div className="pa0">
      <div className="flex flex-wrap justify-center gap4">

        <CircularProgress
          value={totalUsers}
          max={totalMax}
          label="Total Users"
          color="#3b82f6"
        />

        <CircularProgress
          value={activeUsers}
          max={activeMax}
          label="Active Users"
          color="#10b981"
        />

        <CircularProgress
          value={pendingUsers}
          max={pendingMax}
          label="Pending Users"
          color="#f59e0b"
        />

        <CircularProgress
          value={disabledUsers}
          max={totalMax}
          label="Disabled Users"
          color="#ef4444"
        />

      </div>
    </div>
  );
}